import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { publicHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { curationsApiPath } from './apiPath';

const CurationTypeSchema = z.enum(['GOODS', 'SNAP2']);

const GetCurationsRequestParamsSchema = z
  .object({
    snsTitle: z.string(),
    page: z.number().optional(),
    size: z.number().optional(),
    curationId: z.string().optional(),
    type: CurationTypeSchema.optional(),
  })
  .describe('GetCurationsRequestParamsSchema');

type GetCurationsRequestParams = z.infer<typeof GetCurationsRequestParamsSchema>;

// 방문자를 위한 큐레이터 상품 스키마
const VisitorCurationGoodsItemSchema = z
  .object({
    goodsNo: z.number(),
    imageUrl: z.string(),
    isLiked: z.boolean(),
    brandNo: z.number().nullable(),
    brandName: z.string(),
    goodsName: z.string(),
    salesRate: z.number(),
    price: z.number(),
    normalPrice: z.number().nullable(),
    couponPrice: z.number().nullable(),
    couponSalesRate: z.number().nullable(),
    extraDiscountAmount: z
      .number()
      .nullish()
      .describe('추가할인 금액(원). 표시는 finalPrice 사용 (주문 도메인 전용, MUSINSA 전용)'),
    finalPrice: z
      .number()
      .nullish()
      .describe('최종가 = 세일가 + 상품쿠폰 + 추가할인 (장바구니쿠폰 제외, MUSINSA 전용)'),
    finalDiscount: z.number().nullish().describe('정상가 대비 finalPrice 할인율 (MUSINSA 전용)'),
    isLowestPrice: z
      .boolean()
      .nullish()
      .describe('RADAR 추가할인 최저가 보장 여부 — true 시 최저가도전 라벨 (MUSINSA 전용)'),
    isExclusive: z.boolean().nullish().describe('단독 상품 여부 (MUSINSA 전용)'),
    hasOptionPrice: z.boolean().nullable(),
    expectedEarnings: z.number().optional(),
    likeCount: z.number(),
    reviewScore: z.string(),
    reviewCount: z.number(),
    existsCoupon: z.boolean().optional(),
    linkUrl: z.string().optional(),
    isSoldOut: z.boolean(),
    ordNo: z.number().optional(),
    largeCategoryNo: z.number().optional().nullable(),
    largeCategoryName: z.string().optional().nullable(),
    middleCategoryNo: z.number().optional().nullable(),
    middleCategoryName: z.string().optional().nullable(),
    smallCategoryNo: z.number().optional().nullable(),
    smallCategoryName: z.string().optional().nullable(),
  })
  .describe('VisitorCurationGoodsItemSchema');

const VisitorCurationSnapItemSchema = z
  .object({
    id: z.string(),
    imageUrl: z.string(),
    linkUrl: z.string(),
  })
  .describe('VisitorCurationSnapItemSchema');

const VisitorCurationItemSchema = z
  .object({
    curationId: z.string(),
    description: z.string().nullish(),
    title: z.string(),
    type: CurationTypeSchema.nullable().optional(),
    ordNo: z.number(),
    goodsItems: z.array(VisitorCurationGoodsItemSchema),
    snapItems: z.array(VisitorCurationSnapItemSchema).optional().default([]),
  })
  .describe('VisitorCurationItemSchema');

const GetCurationsResponseDataSchema = z.object({
  list: z.array(VisitorCurationItemSchema),
});

const GetCurationsResponseSchema = createApiResponseSchema(GetCurationsResponseDataSchema);

type GetCurationsResponse = z.infer<typeof GetCurationsResponseSchema>;

export const getCurations = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetCurationsRequestParamsSchema),
    async (params: GetCurationsRequestParams): Promise<GetCurationsResponse> =>
      executeStandardRequest(async () => {
        const response = await publicHttpClient.get<GetCurationsResponse>(
          urlJoin(curationsApiPath.getCurations),
          params,
        );
        return response;
      }),
    validateApiResponse(GetCurationsResponseSchema),
  ),
);
